import React, { useState, useEffect, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import api from "../../../utils/axios";
import moment from "moment";
import "moment/locale/th";
import { formatThaiPhone } from "../../../utils/phoneUtils";
import DatePicker from "../../components/DatePickerMonth";
import SearchableSelect from "../../components/SearchableSelect";
import { getData, insertData } from "../../../utils/SamuiUtils";


export default function CustomerModal({ isOpen, onClose, onSave, customer, forceTab }) {
  const [activeTab, setActiveTab] = useState("personal");
  const [formData, setFormData] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [compPrice, setCompPrice] = useState("0.00");
  const [titles, setTitles] = useState([]);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [buildingTypesLoading, setBuildingTypesLoading] = useState(false);

  useEffect(() => {
    const fetchTitles = async () => {
      try {
        const res = await api.get("/get-title");
        if (res.data.status === "OK") {
          setTitles(res.data.result || []);
        }
      } catch (err) {
        console.error("โหลดคำนำหน้าไม่สำเร็จ:", err);
        Swal.fire("ผิดพลาด", "ไม่สามารถโหลดคำนำหน้าชื่อได้", "error");
      }
    };
    fetchTitles();
  }, []);


  const formatPhoneNumber = formatThaiPhone;

  //  ฟังก์ชันฟอร์แมตเลขประจำตัวผู้เสียภาษี
  const formatTaxId = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 13);
    let formatted = digits;
    if (digits.length > 1 && digits.length <= 5)
      formatted = `${digits.slice(0, 1)}-${digits.slice(1)}`;
    else if (digits.length > 5 && digits.length <= 10)
      formatted = `${digits.slice(0, 1)}-${digits.slice(1, 5)}-${digits.slice(5)}`;
    else if (digits.length > 10 && digits.length <= 12)
      formatted = `${digits.slice(0, 1)}-${digits.slice(1, 5)}-${digits.slice(5, 10)}-${digits.slice(10)}`;
    else if (digits.length === 13)
      formatted = `${digits.slice(0, 1)}-${digits.slice(1, 5)}-${digits.slice(5, 10)}-${digits.slice(10, 12)}-${digits.slice(12)}`;
    return formatted;
  };

  // โหลดจังหวัด
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const res = await api.get("/provinces");
        setProvinces(res.data?.data || []);
      } catch (err) {
        console.error("โหลดจังหวัดไม่สำเร็จ:", err);
        Swal.fire("ผิดพลาด", "ไม่สามารถโหลดรายชื่อจังหวัดได้", "error");
      }
    };
    fetchProvinces();
  }, []);

  const normalizeBuildingTypes = useCallback((payload) => {
    const rows = Array.isArray(payload) ? payload : payload?.data || [];
    return rows
      .map((item) => ({
        id:
          item?.ServiceAddr_Type_Id ??
          item?.ServiceAddrType_Id ??
          item?.id ??
          item?.Id,
        name:
          item?.ServiceAddr_Type_Name ??
          item?.ServiceAddrType_Name ??
          item?.name ??
          item?.ServiceAddr_Type,
        status: item?.ServiceAddr_Type_Status ?? item?.status ?? "Y",
      }))
      .filter((item) => item.id && item.name);
  }, []);

  const fetchBuildingTypes = useCallback(async () => {
    setBuildingTypesLoading(true);
    try {
      const res = await getData(
        "Tb_Set_ServiceAddrType",
        "ServiceAddr_Type_Status = 'Y'"
      );
      const normalized = normalizeBuildingTypes(res);
      setBuildingTypes(normalized);
    } catch (err) {
      console.error("โหลดประเภทอาคารไม่สำเร็จ:", err);
      setBuildingTypes([]);
    } finally {
      setBuildingTypesLoading(false);
    }
  }, [normalizeBuildingTypes]);

  useEffect(() => {
    if (!isOpen) return;
    fetchBuildingTypes();
  }, [isOpen, fetchBuildingTypes]);

  // ป้องกัน scroll body เมื่อ modal เปิด
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isOpen]);

  // โหลดข้อมูลลูกค้า
  useEffect(() => {
    if (customer) {
      const displayFormatted =
        customer.CompetitorPrice !== undefined &&
        customer.CompetitorPrice !== null &&
        customer.CompetitorPrice !== "" &&
        !isNaN(customer.CompetitorPrice)
          ? parseFloat(customer.CompetitorPrice).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "0.00";

      const storedValue =
        customer.CompetitorPrice !== undefined &&
        customer.CompetitorPrice !== null &&
        customer.CompetitorPrice !== "" &&
        !isNaN(customer.CompetitorPrice)
          ? parseFloat(customer.CompetitorPrice).toFixed(2)
          : "0.00";

      setFormData({
        AR_TaxNo: customer.AR_TaxNo || "",
        AR_TitleCode: customer.AR_TitleCode || "",
        AR_Name: customer.AR_Name || "",
        AR_LastName: customer.AR_LastName || "",
        DisplayName: customer.DisplayName || "",
        AR_Tel: customer.AR_Tel || "",
        AR_Email: customer.AR_Email || "",
        AR_Line: customer.AR_Line || "",
        AR_ContactName: customer.AR_ContactName || "",
        AR_ContactPosition: customer.AR_ContactPosition || "",
        AR_BuildingType: customer.AR_BuildingType || "",
        AR_Province: customer.AR_Province || "",
        Contract_EndDate: customer.Contract_EndDate
          ? moment(customer.Contract_EndDate).format("YYYY-MM")
          : "",

        Competitor: customer.Competitor || "",
        CompetitorPrice: storedValue,
      });

      setCompPrice(displayFormatted);
      setActiveTab(forceTab ? forceTab : customer.AR_Group === 2 ? "corporate" : "personal");
    } else {
      setFormData({});
      setCompPrice("0.00");
      setActiveTab("personal");
    }
  }, [customer, forceTab]);

  if (!isOpen) return null;

  //  ป้องกันไม่ให้ DatePicker trigger submit
  const handleFormClick = (e) => {
    const isCalendarButton =
      e.target.closest(".btn-outline-secondary") ||
      e.target.classList.contains("rdtPicker") ||
      e.target.closest(".rdtPicker");
    if (isCalendarButton) e.stopPropagation();
  };

  //  เปลี่ยนค่า input พร้อมฟอร์แมตเบอร์โทรและเลขประจำตัวผู้เสียภาษี
  const handleChange = (e) => {
    const { name, value } = e.target;
    const maxLenMap = {
      AR_Name: 200,
      AR_LastName: 200,
      DisplayName: 255,
      AR_ContactName: 150,
      AR_ContactPosition: 150,
      AR_Line: 100,
      AR_Email: 100,
    };

    const nextValue =
      maxLenMap[name] ? String(value).slice(0, maxLenMap[name]) : value;

    if (name === "AR_Tel") {
      const formatted = formatPhoneNumber(value);
      setFormData({ ...formData, [name]: formatted.replace(/-/g, "") });
    } else if (name === "AR_TaxNo") {
      const formatted = formatTaxId(value);
      setFormData({ ...formData, [name]: formatted.replace(/-/g, "") });
    } else if (name === "CompetitorPrice") {
      setFormData({ ...formData, [name]: nextValue });
    } else {
      setFormData({ ...formData, [name]: nextValue });
    }
  };

  const handleAddBuildingType = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: "เพิ่มประเภทอาคาร",
      input: "text",
      inputPlaceholder: "กรอกชื่อประเภทอาคาร",
      confirmButtonText: "บันทึก",
      cancelButtonText: "ยกเลิก",
      showCancelButton: true,
      buttonsStyling: true,
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      willOpen: () => {
        const swalContainer = document.querySelector(".swal2-container");
        if (swalContainer) {
          swalContainer.style.zIndex = "20000";
        }
        const swalBackdrop = document.querySelector(".swal2-backdrop-show");
        if (swalBackdrop) {
          swalBackdrop.style.zIndex = "19999";
        }
      },
    });

    if (!isConfirmed) return;

    const name = String(value || "").trim();
    if (!name) {
      Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณากรอกชื่อประเภทอาคาร", "warning");
      return;
    }

    const result = await insertData(
      "Tb_Set_ServiceAddrType",
      "ServiceAddr_Type_Name, ServiceAddr_Type_Status",
      [name, "Y"]
    );

    if (result?.status === "OK") {
      await fetchBuildingTypes();
      const newRow = Array.isArray(result?.data) ? result.data[0] : null;
      const newId = newRow?.ServiceAddr_Type_Id || newRow?.id;
      if (newId) {
        setFormData((prev) => ({
          ...prev,
          AR_BuildingType: String(newId),
        }));
      }
      Swal.fire("สำเร็จ", "เพิ่มประเภทอาคารแล้ว", "success");
    } else {
      Swal.fire("ผิดพลาด", result?.message || "เพิ่มประเภทอาคารไม่สำเร็จ", "error");
    }
  };

  // ตรวจสอบก่อนบันทึก
  const validateForm = () => {
    const phoneRegex = /^[0-9]{9,10}$/;

    if (activeTab === "personal" && !formData.AR_Name?.trim()) {
      Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณากรอกชื่อ", "warning");
      return false;
    }
    if (activeTab === "corporate" && !formData.AR_Name?.trim()) {
      Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณากรอกชื่อบริษัท", "warning");
      return false;
    }
    if (activeTab === "corporate" && !formData.AR_ContactName?.trim()) {
      Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณากรอกชื่อผู้ติดต่อ", "warning");
      return false;
    }
    if (!customer) {
      if (!phoneRegex.test(formData.AR_Tel)) {
        Swal.fire("เบอร์โทรไม่ถูกต้อง", "กรุณากรอกเบอร์โทร 9–10 หลัก", "error");
        return false;
      }
      if (!formData.AR_BuildingType) {
        Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณาเลือกประเภทอาคาร", "warning");
        return false;
      }
      if (!formData.AR_Province) {
        Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณาเลือกจังหวัด", "warning");
        return false;
      }
    }
    return true;
  };

  //  เมื่อกดบันทึก (ตรวจจับเฉพาะปุ่ม save)
  const handleSubmit = (e) => {
    e.preventDefault();
    const submitter = e.nativeEvent.submitter;
    if (!submitter || submitter.name !== "saveBtn") return; //  กัน submit จาก DatePicker

    if (!validateForm()) return;
    const payload = {
      ...formData,
      AR_Name: formData.AR_Name?.trim() ?? "",
      AR_LastName: formData.AR_LastName?.trim() ?? "",
      AR_ContactName: formData.AR_ContactName?.trim() ?? "",
      AR_Group: activeTab === "personal" ? 1 : 2,
    };
    onSave(activeTab, payload);

    if (!customer) {
      setFormData({});
      setCompPrice("0.00");
    }
  };




    


  const handleClose = () => {
    if (!customer) {
      setFormData({});
      setCompPrice("0.00");
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50" onClick={handleFormClick}>
      <div className="bg-white w-full max-w-[520px] rounded-lg shadow-lg p-6 relative max-h-[95vh] overflow-y-auto">
        <div className="flex items-center mb-6">
          <h3 className="flex items-center gap-2 font-[Open_Sans] text-[26px] font-bold text-[#E13F57] underline underline-offset-4">
            ข้อมูลลูกค้า
          </h3>
          <button onClick={handleClose} className="ml-auto text-gray-400 hover:text-gray-600 text-xl">
            ✕
          </button>
        </div>

        {!forceTab && (
          <div className="w-[360px] h-[58px] mx-auto mb-6 rounded-[10px] border bg-white shadow flex items-center justify-between">
            <div
              onClick={() => setActiveTab("personal")}
              className={`flex-1 flex items-center justify-center cursor-pointer text-[18px] font-bold ${
                activeTab === "personal" ? "text-[#F17F25] underline underline-offset-4" : "text-gray-500"
              }`}
            >
              บุคคลธรรมดา
            </div>
            <div className="w-px h-6 bg-gray-300"></div>
            <div
              onClick={() => setActiveTab("corporate")}
              className={`flex-1 flex items-center justify-center cursor-pointer text-[18px] font-bold ${
                activeTab === "corporate" ? "text-[#F17F25] underline underline-offset-4" : "text-gray-500"
              }`}
            >
              นิติบุคคล
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* 🔹 บุคคลธรรมดา */}
          {activeTab === "personal" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm mb-1 whitespace-nowrap">เลขบัตรประชาชน</label>
                <input
                  type="text"
                  name="AR_TaxNo"
                  maxLength={17}
                  value={formatTaxId(formData.AR_TaxNo || "")}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                />
              </div>

              {/* <div className="col-span-2">
                <label className="block text-sm mb-1">คำนำหน้าชื่อ</label>
                <select
                  name="AR_TitleCode"
                  value={formData.AR_TitleCode || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                >
                  <option value="">เลือก</option>
                  {titles
                    .filter(t => t.Title_Id <= 4)   //  จำกัดเฉพาะบุคคลธรรมดา
                    .map((t) => (
                      <option key={t.Title_Id} value={t.Title_Id}>
                        {t.Title_Name}
                      </option>
                    ))}
                </select>
              </div> */}

              <div className="grid grid-cols-2 col-span-2 gap-3">
                <div>
                  <label className="block text-sm mb-1">
                    ชื่อ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="AR_Name"
                    maxLength={200}
                    value={formData.AR_Name || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">นามสกุล</label>
                  <input
                    type="text"
                    name="AR_LastName"
                    maxLength={200}
                    value={formData.AR_LastName || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>
              </div>

              {customer && (
                <div className="col-span-2">
                  <label className="block text-sm mb-1 whitespace-nowrap">ชื่อที่ใช้ในสัญญา</label>
                  <input
                    type="text"
                    name="DisplayName"
                    maxLength={255}
                    value={formData.DisplayName || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>
              )}

              {!customer && (
                <>
                  <div>
                    <label className="block text-sm mb-1">
                      เบอร์โทรศัพท์ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="AR_Tel"
                      maxLength={12}
                      value={formatPhoneNumber(formData.AR_Tel || "")}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">ไลน์</label>
                    <input
                      type="text"
                      name="AR_Line"
                      value={formData.AR_Line || ""}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm mb-1">อีเมล</label>
                    <input
                      type="email"
                      name="AR_Email"
                      value={formData.AR_Email || ""}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* ---------- นิติบุคคล ---------- */}
          {activeTab === "corporate" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm mb-1">เลขผู้เสียภาษี</label>
                <input
                  type="text"
                  name="AR_TaxNo"
                  maxLength={17}
                  value={formatTaxId(formData.AR_TaxNo || "")}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                />
              </div>

              {/* <div>
                <label className="block text-sm mb-1">คำนำหน้าชื่อ</label>
                <select
                  name="AR_TitleCode"
                  value={formData.AR_TitleCode || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                >
                  <option value="">เลือก</option>
                  {titles
                    .filter(t => t.Title_Id >= 5)   //  เอาเฉพาะบริษัท/หจก/ร้าน
                    .map((t) => (
                      <option key={t.Title_Id} value={t.Title_Id}>
                        {t.Title_Name}
                      </option>
                    ))}
                </select>
              </div> */}

              <div className="col-span-2">
                <label className="block text-sm mb-1">
                  ชื่อบริษัท <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="AR_Name"
                    maxLength={200}
                  value={formData.AR_Name || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                />
              </div>

              {customer && (
                <div className="col-span-2">
                  <label className="block text-sm mb-1 whitespace-nowrap">ชื่อที่ใช้ในสัญญา</label>
                  <input
                    type="text"
                    name="DisplayName"
                    maxLength={255}
                    value={formData.DisplayName || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>
              )}


              {!customer && (
              <>
                <div>
                  <label className="block text-sm mb-1">
                    ผู้ติดต่อ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="AR_ContactName"
                    maxLength={150}
                    value={formData.AR_ContactName || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1 whitespace-nowrap">ตำแหน่งผู้ติดต่อ</label>
                  <input
                    type="text"
                    name="AR_ContactPosition"
                    maxLength={150}
                    value={formData.AR_ContactPosition || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>
              </>
            )}


              {!customer && (
                <>
                  <div>
                    <label className="block text-sm mb-1">
                      เบอร์โทรศัพท์ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="AR_Tel"
                      maxLength={12}
                      value={formatPhoneNumber(formData.AR_Tel || "")}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">ไลน์</label>
                    <input
                      type="text"
                      name="AR_Line"
                      value={formData.AR_Line || ""}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm mb-1">อีเมล</label>
                    <input
                      type="email"
                      name="AR_Email"
                      value={formData.AR_Email || ""}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {!customer && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm whitespace-nowrap">
                    ประเภทอาคาร <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleAddBuildingType}
                    className="text-[#E13F57] hover:text-[#c9354a] text-sm flex items-center gap-1"
                  >
                    <i className="fa fa-plus" />
                  </button>
                </div>
                <select
                  name="AR_BuildingType"
                  value={formData.AR_BuildingType || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                  disabled={buildingTypesLoading}
                >
                  <option value="">
                    {buildingTypesLoading ? "กำลังโหลด..." : "เลือก"}
                  </option>
                  {buildingTypes.map((type) => (
                    <option key={type.id} value={String(type.id)}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1">
                  จังหวัด <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  value={formData.AR_Province || ""}
                  onChange={(val) =>
                    handleChange({ target: { name: "AR_Province", value: val } })
                  }
                  options={provinces.map((p) => ({
                    value: p.ProvinceId,
                    label: p.NameInThai,
                  }))}
                  placeholder="เลือกจังหวัด"
                  searchPlaceholder="ค้นหาจังหวัด..."
                />
              </div>
            </div>
          )}

          {customer && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="col-span-2">
                <label className="block text-sm mb-1 whitespace-nowrap">วันที่หมดสัญญา</label>
                  <DatePicker
                    name="Contract_EndDate"
                    value={formData.Contract_EndDate || ""}
                    mode="month" //  ใช้โหมดเลือกเดือน-ปี
                    onChange={(val) =>
                      handleChange({ target: { name: "Contract_EndDate", value: val } })
                    }
                  />
              </div>

              <div>
                <label className="block text-sm mb-1">ชื่อคู่แข่ง</label>
                <input
                  type="text"
                  name="Competitor"
                  value={formData.Competitor || ""}
                  onChange={handleChange}
                  placeholder="ชื่อคู่แข่ง (ถ้ามี)"
                  className="w-full border rounded px-3 py-2 h-[42px]"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">ราคาคู่แข่ง</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="CompetitorPrice"
                  value={compPrice}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    let val = e.target.value.replace(/,/g, "");
                    if (!/^\d*\.?\d*$/.test(val)) return;
                    if (val.startsWith("-")) return;

                    const [intRaw, decRaw] = val.split(".");
                    let intPart = intRaw || "";
                    let decPart = decRaw || "";

                    if (decPart.length > 2) decPart = decPart.slice(0, 2);
                    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                    const formatted = decRaw !== undefined ? `${intPart}.${decPart}` : intPart;
                    setCompPrice(formatted);
                    handleChange({ target: { name: "CompetitorPrice", value: val } });
                  }}
                  onBlur={() => {
                    let raw = compPrice.replace(/,/g, "");
                    let v = parseFloat(raw);
                    if (isNaN(v) || v < 0) v = 0;

                    const formatted = v.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });
                    setCompPrice(formatted);

                    handleChange({
                      target: { name: "CompetitorPrice", value: v.toFixed(2) },
                    });
                  }}
                  className="w-full border rounded px-3 py-2 h-[42px] text-right"
                  placeholder="0.00"
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 mt-6">
            <button
              type="submit"
              name="saveBtn"
              className="bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg"
            >
              {customer ? "อัปเดต" : "บันทึก"}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="border border-red-400 text-red-500 py-2 rounded-lg"
            >
              ยกเลิก
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
